<template>
  <iColumn
    ref="scrollRoot"
    height="fill"
    width="fill"
    gap="none"
    overflow="scroll"
    wrap="nowrap"
  >
    <iRow align="center" :height="80" vertical-align="middle">
      <iColumn :max-width="900" width="fill" align="left">
        <iRow align="left" wrap="nowrap" vertical-align="middle">
          <Logo />
        </iRow>
      </iColumn>
    </iRow>

    <iRow align="center" width="fill">
      <iColumn :max-width="680" width="fill" align="center">
        <iSpace :height="40" />

        <iRow v-if="!isSubmitted && currentStepDefinition" align="center" width="fill">
          <iColumn width="fill" gap="extraSmall">
            <iText font-size="small" variant="branded">
              STEP {{ currentStep }} OF {{ totalSteps }}
            </iText>
            <QuestionnaireProgressBar :percent="progressPercent" />
          </iColumn>
        </iRow>

        <iSpace v-if="!isSubmitted" :height="24" />

        <iColumn v-if="!isSubmitted && currentStepDefinition" gap="extraLarge" width="fill">
          <iColumn gap="extraSmall">
            <iHeading font-size="extraExtraLarge">
              {{ currentStepDefinition.title }}
            </iHeading>
            <iText variant="subtle">
              {{ currentStepDefinition.description }}
            </iText>
          </iColumn>

          <iCard
            v-for="section in currentStepDefinition.sections"
            :key="section.key"
            width="fill"
          >
            <template #body>
              <iColumn :gap="fieldGapForSection(section)" width="fill">
                <QuestionnaireField
                  v-for="field in section.fields"
                  :key="field.key"
                  :section-key="section.key"
                  :field="field"
                />
              </iColumn>
            </template>
          </iCard>
        </iColumn>

        <iRow v-if="submitError && !isSubmitted" width="fill">
          <iAlert variant="error">
            <template #title>
              <iText>{{ submitError }}</iText>
            </template>
          </iAlert>
        </iRow>

        <iRow
          v-if="!isSubmitted && currentStepDefinition"
          width="fill"
          align="between"
          wrap="wrap"
          gap="standard"
        >
          <iButton
            v-if="currentStep > 1"
            variant="secondary"
            width="hug"
            @click="goToPreviousStep"
          >
            Back
          </iButton>
          <iColumn v-else width="hug" />
          <iButton
            v-if="currentStep < totalSteps"
            variant="primary"
            width="hug"
            @click="attemptGoToNextStep"
          >
            Continue
          </iButton>
          <iButton
            v-else
            variant="branded"
            width="hug"
            :is-loading="isSubmitting"
            @click="attemptSubmitCreatorQuestionnaire"
          >
            Submit
          </iButton>
        </iRow>

        <iColumn
          v-if="isSubmitted"
          gap="large"
          width="fill"
          align="center"
        >
          <iSpace :height="40" />
          <iHeading font-size="extraExtraLarge" align="center">
            You're all done{{ submittedFirstName ? `, ${submittedFirstName}` : "" }}!
          </iHeading>
          <iText variant="subtle" text-align="center">
            Thank you for taking the time to share your vision with us. We've received everything and are already
            looking forward to bringing it to life.
          </iText>

          <iCard width="fill">
            <template #body>
              <iColumn gap="standard" width="fill">
                <iText font-size="small" variant="branded">
                  WHAT HAPPENS NEXT
                </iText>
                <iColumn gap="standard" width="fill">
                  <iRow gap="standard" wrap="nowrap">
                    <iColumn width="hug">
                      <iText font-size="large" variant="branded">
                        1.
                      </iText>
                    </iColumn>
                    <iColumn width="fill" gap="extraSmall">
                      <iText font-weight="dark">
                        We review your answers
                      </iText>
                      <iText variant="subtle">
                        Our team carefully reads through everything you've shared and begins shaping your platform concept.
                      </iText>
                    </iColumn>
                  </iRow>
                  <iRow gap="standard" wrap="nowrap">
                    <iColumn width="hug">
                      <iText font-size="large" variant="branded">
                        2.
                      </iText>
                    </iColumn>
                    <iColumn width="fill" gap="extraSmall">
                      <iText font-weight="dark">
                        We'll be in touch within 1-2 business days
                      </iText>
                      <iText variant="subtle">
                        Expect a personalized follow-up with your next steps, timeline, and any questions we have.
                      </iText>
                    </iColumn>
                  </iRow>
                  <iRow gap="standard" wrap="nowrap">
                    <iColumn width="hug">
                      <iText font-size="large" variant="branded">
                        3.
                      </iText>
                    </iColumn>
                    <iColumn width="fill" gap="extraSmall">
                      <iText font-weight="dark">
                        We start building
                      </iText>
                      <iText variant="subtle">
                        Once we're aligned on the vision, we get to work crafting your platform from the ground up.
                      </iText>
                    </iColumn>
                  </iRow>
                </iColumn>
              </iColumn>
            </template>
          </iCard>

          <iCard width="fill">
            <template #body>
              <iColumn gap="extraSmall" width="fill">
                <iText font-size="small" variant="branded">
                  QUESTIONS IN THE MEANTIME?
                </iText>
                <iText>
                  Reach out to <strong>Arianna Pillay</strong>
                </iText>
                <iLink href="mailto:arianna@inspiration.co">
                  arianna@inspiration.co
                </iLink>
              </iColumn>
            </template>
          </iCard>
        </iColumn>

        <iSpace :height="60" />
      </iColumn>
    </iRow>
  </iColumn>
</template>

<script>
import { mapActions, mapState } from "pinia";
import Logo from "@/components/Logo";
import QuestionnaireField from "@/components/questionnaire/QuestionnaireField.vue";
import QuestionnaireProgressBar from "@/components/questionnaire/QuestionnaireProgressBar.vue";
import { useQuestionnaireStore } from "@/stores/questionnaire-store";

export default {
  name: "QuestionnaireForm",
  components: { Logo, QuestionnaireField, QuestionnaireProgressBar },
  computed: {
    ...mapState(useQuestionnaireStore, [
      "questionnaireSchema",
      "responseData",
      "currentStep",
      "totalSteps",
      "progressPercent",
      "currentStepDefinition",
      "isSubmitting",
      "isSubmitted",
      "submitError",
    ]),
    submittedFirstName() {
      return (this.responseData.contact || {}).first_name || "";
    },
  },
  watch: {
    // The form body scrolls inside the overflow="scroll" column, not the
    // window, so advancing a step has to reset that container's scrollTop -
    // otherwise the next step opens mid-page with the progress bar cut off.
    currentStep() {
      this.scrollFormToTop();
    },
    isSubmitted() {
      this.scrollFormToTop();
    },
  },
  created() {
    this.ensureResponseDataDefaults();
  },
  methods: {
    ...mapActions(useQuestionnaireStore, [
      "ensureResponseDataDefaults",
      "attemptGoToNextStep",
      "attemptSubmitCreatorQuestionnaire",
      "goToPreviousStep",
    ]),
    scrollFormToTop() {
      this.$nextTick(() => {
        const scrollElement = this.$refs.scrollRoot && this.$refs.scrollRoot.$el;
        if (scrollElement && typeof scrollElement.scrollTo === "function") {
          scrollElement.scrollTo({ top: 0, behavior: "smooth" });
        }
      });
    },
    // Name fields cluster together more tightly than the longer prompts in
    // later sections, so the contact section uses half the standard gap.
    fieldGapForSection(section) {
      if (section.key === "contact") {
        return 24;
      }
      return 48;
    },
  },
};
</script>
